import { useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useDialogFocus } from '../../../../hooks/useDialogFocus';
import { useLayerEscape } from '../../../../hooks/useLayerEscape';
import { useBrowserOverlayHost } from './browserOverlayHost';

interface BrowserOverlayProps {
    label: string;
    onClose: () => void;
    className?: string;
    initialFocusRef?: RefObject<HTMLElement>;
    children: ReactNode;
}

// Covers the whole browser modal at its fixed size; Escape and Tab stay inside this layer.
export function BrowserOverlay({
    label,
    onClose,
    className = '',
    initialFocusRef,
    children
}: BrowserOverlayProps) {
    const host = useBrowserOverlayHost();
    const layerRef = useRef<HTMLDivElement>(null);
    useDialogFocus(layerRef, initialFocusRef);
    useLayerEscape(onClose);

    return createPortal(
        <div
            ref={layerRef}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            tabIndex={-1}
            className={`${host ? 'absolute' : 'fixed'} inset-0 z-20 flex flex-col focus:outline-none ${className}`}
        >
            {children}
        </div>,
        host ?? document.body
    );
}
